import React, { useState, useEffect, useContext } from 'react';
import {
  CalendarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import { AuthContext } from '../../App';
import apiClient from '../../utils/apiClient';
import { format, isToday, isTomorrow, isThisWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import { isGuest as checkIsGuest } from '../../utils/roleUtils';
import EventModal from '../EventModal';

interface Event {
  id: string;
  title: string;
  start: string;
  start_time: string;
  end_time?: string;
  location?: string;
  participantCount: number;
  max_teilnehmer: number;
  creator: string | null;
  creator_name: string;
  cotrainer: string | null;
  cotrainer_name: string | null;
  farbe?: string;
}

const UpcomingEvents: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const isGuest = checkIsGuest(user);
  const [events, setEvents] = useState<Event[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (user) {
      fetchEvents();
    }
  }, [user]);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/calendar/user-events/unknown');
      const data = response.data;

      if (!data || !Array.isArray(data.eventsBooked)) {
        throw new Error('Keine Events gefunden');
      }

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      // Filter and sort upcoming events (including today)
      const upcomingEvents = data.eventsBooked
        .filter((event: any) => {
          if (!event?.start || !event?.start_time) return false;
          const eventDate = new Date(`${event.start}T${event.start_time}`);
          return !isNaN(eventDate.getTime()) && eventDate >= now;
        })
        .sort((a: any, b: any) => {
          const dateA = new Date(`${a.start}T${a.start_time}`);
          const dateB = new Date(`${b.start}T${b.start_time}`);
          return dateA.getTime() - dateB.getTime();
        })
        .slice(0, 2) // Take only next 2 events
        .map((event: any) => ({
          id: event.id || `temp-${Math.random()}`,
          title: event.title || 'Unbenannter Termin',
          start: event.start,
          start_time: event.start_time,
          end_time: event.end_time,
          location: event.location,
          participantCount: event.participantCount || 0,
          max_teilnehmer: event.max_teilnehmer || 0,
          creator: event.creator || null,
          creator_name: event.creator_name || 'Unbekannt',
          cotrainer: event.cotrainer || null,
          cotrainer_name: event.cotrainer_name || null,
          farbe: event.farbe || '#6B7280',
        }));

      setEvents(upcomingEvents);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching events:', err);
        setError('Fehler beim Laden der Termine');
      }
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const getMemberImageUrl = (memberNr: string | null) => {
    if (!memberNr) return null;
    const token = localStorage.getItem('jwt_token');
    return `${BASE_URL}/api/members/user-image-url/${memberNr}?size=S&token=${token}`;
  };

  const getDateLabel = (dateStr: string): string => {
    const date = new Date(dateStr);
    if (isToday(date)) return 'Heute';
    if (isTomorrow(date)) return 'Morgen';
    if (isThisWeek(date, { weekStartsOn: 1 })) {
      return format(date, 'EEEE', { locale: de });
    }
    return format(date, 'dd.MM.yyyy', { locale: de });
  };

  const getParticipantStatus = (current: number, max: number) => {
    const percentage = (current / max) * 100;
    if (percentage >= 100) return { color: 'bg-error-500', text: 'Ausgebucht' };
    if (percentage >= 80) return { color: 'bg-warning-500', text: 'Fast voll' };
    return { color: 'bg-success-500', text: 'Plätze frei' };
  };

  const handleEventClick = async (event: Event) => {
    try {
      // Event-Details vom Backend laden
      const response = await apiClient.get('/api/calendar');
      const data = response.data;

      if (data.success && data.events) {
        // Finde das vollständige Event-Objekt
        const fullEvent = data.events.find((e: any) => e.id === event.id);

        if (fullEvent) {
          // Konvertiere zu Calendar-Format
          const [startHour, startMinute] = event.start_time.split(':');
          const [endHour, endMinute] = event.end_time?.split(':') || ['00', '00'];

          const startDate = new Date(event.start);
          startDate.setHours(parseInt(startHour), parseInt(startMinute), 0, 0);

          const endDate = new Date(event.start);
          endDate.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);

          const formattedEvent = {
            id: fullEvent.id,
            title: fullEvent.title,
            start: startDate,
            end: endDate,
            extendedProps: {
              color: fullEvent.color || event.farbe,
              bookable: parseInt(fullEvent.bookable) || 0,
              bookingCount: parseInt(fullEvent.booking_count) || event.participantCount,
              creator: {
                name: fullEvent.creator?.name || event.creator_name,
                id: fullEvent.creator?.id || event.creator,
                email: fullEvent.creator?.email,
                image: fullEvent.creator?.image,
              },
              cotrainer: fullEvent.cotrainer ? {
                name: fullEvent.cotrainer.name || event.cotrainer_name,
                id: fullEvent.cotrainer.id || event.cotrainer,
                email: fullEvent.cotrainer.email,
                image: fullEvent.cotrainer.image,
              } : null,
              repeat: fullEvent.repeat,
              repeat_end: fullEvent.repeat_end,
              unique_id: fullEvent.unique_id,
            },
          };

          setSelectedEvent(formattedEvent);
          setShowModal(true);
        }
      }
    } catch (err) {
      console.error('Error loading event details:', err);
    }
  };

  const closeModal = () => {
    setSelectedEvent(null);
    setShowModal(false);
  };

  const handleDeleteEvent = () => {
    // Nach Löschen Modal schließen und Events neu laden
    closeModal();
    fetchEvents();
  };

  const handleBookEvent = () => {
    // Nach Buchung Events neu laden
    fetchEvents();
  };

  if (loading) {
    return (
      <div className="mb-4">
        <div className="bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 animate-pulse">
            {[1, 2].map((i) => (
              <div key={i} className="bg-gray-50 dark:bg-[#1a2332] rounded-xl p-4 border border-gray-100 dark:border-gray-700/30 relative overflow-hidden">
                {/* Farbiger Border-Accent Skeleton */}
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gray-300 dark:bg-gray-600 rounded-l-xl"></div>

                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                  <div className="flex-1 space-y-3">
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                    <div className="flex gap-3">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                    </div>
                  </div>
                  <div className="w-16 h-10 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return null; // Hide widget if no events
  }

  return (
    <div>
      <div className="bg-white dark:bg-[#1c2432] rounded-xl sm:rounded-2xl border border-gray-200 dark:border-gray-700 p-3 sm:p-5">
        <div className="flex items-center gap-2 sm:gap-3 mb-2.5 sm:mb-4">
          <div className="w-7 h-7 sm:w-10 sm:h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg sm:rounded-xl flex items-center justify-center">
            <CalendarIcon className="h-3.5 w-3.5 sm:h-5 sm:w-5 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">
            Deine nächsten Buchungen
          </h2>
        </div>

        <div>
          {error && (
            <div className="bg-error-50 dark:bg-error-900/20 rounded-lg p-4 border border-error-200 dark:border-error-700 mb-4">
              <div className="flex items-center gap-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-error-500" />
                <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
                <button 
                  onClick={() => setError(null)} 
                  className="ml-auto text-error-500 hover:text-error-700"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {events.length > 0 ? (
            <div className="space-y-3">
              {events.map((event, index) => {
                const status = getParticipantStatus(event.participantCount, event.max_teilnehmer);
                const dateLabel = getDateLabel(event.start);

                return (
                  <div
                    key={event.id}
                    className="relative bg-gray-50 dark:bg-[#1a2332] rounded-xl border border-gray-100 dark:border-gray-700/30
                              hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600/50
                              transition-all duration-200 cursor-pointer
                              overflow-hidden group"
                  >
                    {/* Farbiger Border-Accent links */}
                    <div
                      className="absolute left-0 top-0 bottom-0 w-1 transition-all duration-200 group-hover:w-1.5"
                      style={{ backgroundColor: event.farbe }}
                    />

                    {/* Mobile Layout */}
                    <div className="sm:hidden p-2.5 pl-3.5" onClick={() => handleEventClick(event)}>
                      <div className="flex items-center gap-2.5">
                        {/* Avatar */}
                        <div className="relative flex-shrink-0">
                          {event.creator && (
                            <div
                              className="w-10 h-10 rounded-full border-2 shadow-sm"
                              style={{ borderColor: event.farbe }}
                            >
                              {!imageErrors[`${event.id}-creator`] && getMemberImageUrl(event.creator) ? (
                                <img
                                  src={getMemberImageUrl(event.creator)!}
                                  alt={event.creator_name}
                                  className="w-full h-full object-cover rounded-full"
                                  onError={() => setImageErrors(prev => ({ ...prev, [`${event.id}-creator`]: true }))}
                                />
                              ) : (
                                <div
                                  className="w-full h-full rounded-full flex items-center justify-center"
                                  style={{
                                    backgroundColor: event.farbe
                                  }}
                                >
                                  <UserIcon className="h-6 w-6 text-white/90" />
                                </div>
                              )}
                            </div>
                          )}
                          {event.cotrainer && (
                            <div
                              className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full border-2 border-white dark:border-gray-800 shadow-sm"
                              style={{ backgroundColor: event.farbe }}
                            >
                              {!imageErrors[`${event.id}-cotrainer`] && getMemberImageUrl(event.cotrainer) ? (
                                <img
                                  src={getMemberImageUrl(event.cotrainer)!}
                                  alt={event.cotrainer_name || ''}
                                  className="w-full h-full object-cover rounded-full"
                                  onError={() => setImageErrors(prev => ({ ...prev, [`${event.id}-cotrainer`]: true }))}
                                />
                              ) : (
                                <div className="w-full h-full rounded-full flex items-center justify-center">
                                  <UserIcon className="h-3 w-3 text-white/90" />
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm flex items-center gap-1.5">
                              <AcademicCapIcon
                                className="h-3.5 w-3.5 flex-shrink-0"
                                style={{ color: event.farbe }}
                              />
                              <span className="truncate">{event.title}</span>
                            </h4>
                            <div
                              className="flex items-center gap-1 px-2 py-0.5 rounded-lg flex-shrink-0"
                              style={{
                                backgroundColor: `${event.farbe}20`,
                                color: event.farbe
                              }}
                            >
                              <UserGroupIcon className="h-3.5 w-3.5" style={{ color: event.farbe }} />
                              <span className="font-bold text-xs">{event.participantCount}/{event.max_teilnehmer}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                              <CalendarIcon className="h-3.5 w-3.5" style={{ color: event.farbe }} />
                              <span>{format(new Date(event.start), 'dd.MM.yyyy')}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <ClockIcon className="h-3.5 w-3.5" style={{ color: event.farbe }} />
                              <span>{event.start_time}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:block p-3 pl-4" onClick={() => handleEventClick(event)}>
                      <div className="flex items-center justify-between gap-4">
                        {/* Left: Avatar + Title */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {/* Avatar */}
                          <div className="relative flex-shrink-0">
                            {event.creator && (
                              <div
                                className="w-11 h-11 rounded-full border-2 shadow-sm transition-transform duration-200 group-hover:scale-105"
                                style={{ borderColor: event.farbe }}
                              >
                                {!imageErrors[`${event.id}-creator`] && getMemberImageUrl(event.creator) ? (
                                  <img
                                    src={getMemberImageUrl(event.creator)!}
                                    alt={event.creator_name}
                                    className="w-full h-full object-cover rounded-full"
                                    onError={() => setImageErrors(prev => ({ ...prev, [`${event.id}-creator`]: true }))}
                                  />
                                ) : (
                                  <div
                                    className="w-full h-full rounded-full flex items-center justify-center"
                                    style={{
                                      backgroundColor: event.farbe
                                    }}
                                  >
                                    <UserIcon className="h-5 w-5 text-white/90" />
                                  </div>
                                )}
                              </div>
                            )}
                            {event.cotrainer && (
                              <div
                                className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white dark:border-gray-800 shadow-sm"
                                style={{ backgroundColor: event.farbe }}
                              >
                                {!imageErrors[`${event.id}-cotrainer`] && getMemberImageUrl(event.cotrainer) ? (
                                  <img
                                    src={getMemberImageUrl(event.cotrainer)!}
                                    alt={event.cotrainer_name || ''}
                                    className="w-full h-full object-cover rounded-full"
                                    onError={() => setImageErrors(prev => ({ ...prev, [`${event.id}-cotrainer`]: true }))}
                                  />
                                ) : (
                                  <div className="w-full h-full rounded-full flex items-center justify-center">
                                    <UserIcon className="h-2.5 w-2.5 text-white/90" />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Title */}
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <AcademicCapIcon
                              className="h-4 w-4 flex-shrink-0"
                              style={{ color: event.farbe }}
                            />
                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                              {event.title}
                            </h4>
                          </div>
                        </div>

                        {/* Right: Date + Time + Participants */}
                        <div className="flex items-center gap-4 flex-shrink-0">
                          {/* Date */}
                          <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                            <CalendarIcon className="h-4 w-4" style={{ color: event.farbe }} />
                            <span>{format(new Date(event.start), 'dd.MM.yyyy')}</span>
                          </div>

                          {/* Time */}
                          <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                            <ClockIcon className="h-4 w-4" style={{ color: event.farbe }} />
                            <span>{event.start_time}</span>
                          </div>

                          {/* Participants */}
                          <div
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg shadow-sm transition-all duration-200 group-hover:scale-105"
                            style={{
                              backgroundColor: `${event.farbe}20`,
                              color: event.farbe
                            }}
                          >
                            <UserGroupIcon className="h-4 w-4" style={{ color: event.farbe }} />
                            <span className="font-bold text-sm">
                              {event.participantCount}/{event.max_teilnehmer}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : isGuest ? (
            <div className="text-center py-8">
              <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 dark:text-gray-400 font-medium">
                Kein Training gebucht
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Samstags-Training im Kalender buchbar
              </p>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-500 dark:text-gray-400">
                Keine anstehenden Termine
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Event Modal */}
      {showModal && selectedEvent && (
        <EventModal
          event={selectedEvent}
          events={[selectedEvent]}
          onClose={closeModal}
          onDelete={handleDeleteEvent}
          onBook={handleBookEvent}
          refreshCalendar={fetchEvents}
          onEventSelect={setSelectedEvent}
          onEdit={() => {}}
        />
      )}
    </div>
  );
};

export default UpcomingEvents;